import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import { Badge, Segmented, Switch } from "../../../components/ui";
import { apiMessage, runReminder, updateReminder } from "../../../services/notifications";
import { currentSession } from "../../../utils/roles";
import { PhonePreview } from "./Composer";

// Monday first, values as in JS (0 = Sunday)
const DAYS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 0, label: "Sun" },
];
const TIMEZONES = ["Africa/Abidjan", "Africa/Lagos", "Africa/Douala", "Africa/Kinshasa", "Africa/Johannesburg", "Europe/Paris", "America/New_York"];
const pad = (n) => String(n).padStart(2, "0");

const PUSH_FIELDS = [
  { key: "pushTitle", label: "Notification title" },
  { key: "pushBodyLapsed", label: "Notification text (former players)" },
  { key: "pushBodyNever", label: "Notification text (never played)" },
];
const EMAIL_FIELDS = [
  { key: "subjectLapsed", label: "Subject (former players)" },
  { key: "bodyLapsed", label: "Message (former players)", multiline: true },
  { key: "subjectNever", label: "Subject (never played)" },
  { key: "bodyNever", label: "Message (never played)", multiline: true },
  { key: "button", label: "Button" },
];

/** Figures of a channel: who would receive it (preview), or who received it (real send). */
const Figures = ({ label, figures, sent }) =>
  figures ? (
    <div>
      <strong>{sent ? figures.sent || 0 : figures.audience}</strong>
      <span>{label}</span>
    </div>
  ) : null;

/**
 * Reminder sent by notification, by email or both (today: the quiz reminder), next to the other
 * automations: schedule, channels, audience, texts, preview, test email, send now.
 */
const ReminderAutomation = ({ automation, onSaved }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(automation);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirmRun, setConfirmRun] = useState(false);
  const [english, setEnglish] = useState(false);
  const [testEmail, setTestEmail] = useState(() => currentSession().email || "");

  useEffect(() => setForm(automation), [automation]);

  const dirty = JSON.stringify(form) !== JSON.stringify(automation);
  const set = (changes) => setForm((f) => ({ ...f, ...changes }));
  const setSetting = (key, value) => setForm((f) => ({ ...f, settings: { ...f.settings, [key]: value } }));
  const toggleDay = (day) => set({ days: form.days.includes(day) ? form.days.filter((d) => d !== day) : [...form.days, day] });
  const channels = form.settings.channels || "both";
  const withPush = channels !== "email";
  const withEmail = channels !== "push";

  const save = async (changes = {}) => {
    setSaving(true);
    try {
      const saved = await updateReminder(automation.key, {
        enabled: form.enabled,
        days: form.days,
        hour: form.hour,
        minute: form.minute,
        timezone: form.timezone,
        settings: form.settings,
        ...changes,
      });
      onSaved(saved);
      toast.success(t("Automation saved"));
      return true;
    } catch (error) {
      toast.error(apiMessage(error, t("Something went wrong")));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const run = async (kind, body) => {
    if (dirty && !(await save())) return;
    setBusy(kind);
    try {
      const result = await runReminder(automation.key, body);
      if (kind === "preview") setPreview({ ...result, sent: false });
      if (kind === "test") toast[result.test === "sent" ? "success" : "error"](result.test === "sent" ? t("Test email sent to {{email}}", { email: testEmail }) : t("The test email could not be sent"));
      if (kind === "run") {
        setPreview({ ...result, sent: true });
        toast.success(t("{{push}} notification(s) and {{email}} email(s) sent", { push: result.push?.sent || 0, email: result.email?.sent || 0 }));
        onSaved(null);
      }
    } catch (error) {
      toast.error(apiMessage(error, t("Something went wrong")));
    } finally {
      setBusy("");
    }
  };

  const suffix = english ? "_en" : "";
  const last = automation.lastResult;
  const textField = (field) => (
    <div key={field.key} className="ui-field ui-field-full">
      <label htmlFor={`${automation.key}-${field.key}${suffix}`}>{t(field.label)}</label>
      {field.multiline ? (
        <textarea id={`${automation.key}-${field.key}${suffix}`} className="ui-input email-auto-textarea" rows={5} value={form.settings[`${field.key}${suffix}`] || ""} onChange={(e) => setSetting(`${field.key}${suffix}`, e.target.value)} />
      ) : (
        <input id={`${automation.key}-${field.key}${suffix}`} className="ui-input" value={form.settings[`${field.key}${suffix}`] || ""} onChange={(e) => setSetting(`${field.key}${suffix}`, e.target.value)} />
      )}
    </div>
  );
  const pushSample = preview?.preview?.push || {
    title: form.settings[`pushTitle${suffix}`],
    body: (form.settings[`pushBodyLapsed${suffix}`] || "").replace("{prenom}", "Awa").replace("{score}", "85"),
  };

  return (
    <section className="ui-card notif-automation">
      <header className="notif-automation-head">
        <span className="notif-automation-icon" aria-hidden="true">
          <i className="ri-trophy-line"></i>
        </span>
        <div className="notif-automation-title">
          <h2>{t(automation.label)}</h2>
          <p>{t(automation.description)}</p>
        </div>
        <Switch
          checked={form.enabled}
          onChange={(enabled) => {
            set({ enabled });
            save({ enabled });
          }}
          label={t("Enabled")}
          onLabel={t("Enabled")}
          offLabel={t("Disabled")}
        />
      </header>

      <div className="notif-automation-body">
        <div className="notif-automation-settings">
          <div className="notif-section">
            <span className="ui-label">{t("Send by")}</span>
            <Segmented
              label={t("Send by")}
              value={channels}
              onChange={(value) => setSetting("channels", value)}
              options={[
                { value: "push", label: t("Notification only") },
                { value: "email", label: t("Email only") },
                { value: "both", label: t("Both") },
              ]}
            />
          </div>

          <div className="notif-section">
            <span className="ui-label">{t("Days")}</span>
            <div className="ui-chips">
              {DAYS.map((day) => (
                <button key={day.value} type="button" className="ui-chip" aria-pressed={form.days.includes(day.value)} onClick={() => toggleDay(day.value)}>
                  {t(day.label)}
                </button>
              ))}
            </div>
          </div>

          <div className="ui-form">
            <div className="ui-field">
              <label htmlFor={`${automation.key}-time`}>{t("Time")}</label>
              <input
                id={`${automation.key}-time`}
                type="time"
                className="ui-input"
                value={`${pad(form.hour)}:${pad(form.minute)}`}
                onChange={(e) => {
                  const [hour, minute] = e.target.value.split(":").map(Number);
                  if (!Number.isNaN(hour)) set({ hour, minute: minute || 0 });
                }}
              />
            </div>
            <div className="ui-field">
              <label htmlFor={`${automation.key}-tz`}>{t("Time zone")}</label>
              <select id={`${automation.key}-tz`} className="ui-input" value={form.timezone} onChange={(e) => set({ timezone: e.target.value })}>
                {[...new Set([form.timezone, ...TIMEZONES])].map((tz) => (
                  <option key={tz} value={tz}>
                    {tz.replace("_", " ")}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="notif-section">
            <span className="ui-label">{t("Who receives it")}</span>
            <Segmented
              label={t("Who receives it")}
              value={form.settings.audience}
              onChange={(value) => setSetting("audience", value)}
              options={[
                { value: "both", label: t("Former players and new ones") },
                { value: "lapsed", label: t("Former players") },
                { value: "never", label: t("Never played") },
              ]}
            />
          </div>

          <div className="ui-form">
            {form.settings.audience !== "never" && (
              <div className="ui-field">
                <label htmlFor={`${automation.key}-inactive`}>{t("Former player: no game for (days)")}</label>
                <input id={`${automation.key}-inactive`} type="number" min={1} max={365} className="ui-input" value={form.settings.inactiveDays} onChange={(e) => setSetting("inactiveDays", Number(e.target.value))} />
              </div>
            )}
            <div className="ui-field">
              <label htmlFor={`${automation.key}-cooldown`}>{t("At most one reminder every (days)")}</label>
              <input id={`${automation.key}-cooldown`} type="number" min={1} max={365} className="ui-input" value={form.settings.cooldownDays} onChange={(e) => setSetting("cooldownDays", Number(e.target.value))} />
            </div>
            {withEmail && (
              <div className="ui-field">
                <label htmlFor={`${automation.key}-max`}>{t("Emails per send at most")}</label>
                <input id={`${automation.key}-max`} type="number" min={1} max={5000} className="ui-input" value={form.settings.maxPerRun} onChange={(e) => setSetting("maxPerRun", Number(e.target.value))} />
              </div>
            )}
            {withEmail && form.settings.audience !== "lapsed" && (
              <div className="ui-field notif-automation-switch">
                <Switch
                  checked={Boolean(form.settings.appUsersOnly)}
                  onChange={(value) => setSetting("appUsersOnly", value)}
                  label={t("Email to people who never played: only those who use the app")}
                  onLabel={t("Email to people who never played: only those who use the app")}
                  offLabel={t("Email to people who never played: only those who use the app")}
                />
              </div>
            )}
          </div>

          <div className="notif-section">
            <div className="notif-automation-texts-head">
              <span className="ui-label">{t("Texts")}</span>
              <div className="ui-chips">
                <button type="button" className="ui-chip" aria-pressed={!english} onClick={() => setEnglish(false)}>
                  {t("French")}
                </button>
                <button type="button" className="ui-chip" aria-pressed={english} onClick={() => setEnglish(true)}>
                  {t("English")}
                </button>
              </div>
            </div>
            {withPush && (
              <>
                <h3 className="reminder-texts-title">
                  <i className="ri-notification-3-line" aria-hidden="true"></i> {t("Notification")}
                </h3>
                <div className="ui-form">{PUSH_FIELDS.map(textField)}</div>
              </>
            )}
            {withEmail && (
              <>
                <h3 className="reminder-texts-title">
                  <i className="ri-mail-line" aria-hidden="true"></i> {t("Email")}
                </h3>
                <div className="ui-form">{EMAIL_FIELDS.map(textField)}</div>
              </>
            )}
            <p className="ui-hint">
              {t("Variables: {prenom} first name, {score} best quiz score, {parties} number of games. In the email, leave an empty line between two paragraphs. The notification and the email button open the game in the app.")}
            </p>
          </div>
        </div>

        <aside className="notif-preview-col email-auto-preview">
          {withPush && <PhonePreview title={pushSample.title} body={pushSample.body} />}
          {withEmail &&
            (preview?.preview ? (
              <>
                <p className="email-auto-subject">
                  <span>{t("Subject")} :</span> {preview.preview.subject}
                </p>
                <iframe title={t("Email preview")} className="email-auto-frame" srcDoc={preview.preview.html} sandbox="" />
              </>
            ) : (
              <p className="ui-hint">{t("Click « Preview » to see the email and who receives it.")}</p>
            ))}
          {preview && (
            <div className="notif-automation-result">
              <Figures label={preview.sent ? t("notifications sent") : t("would get the notification")} figures={preview.push} sent={preview.sent} />
              <Figures label={preview.sent ? t("emails sent") : t("would get the email")} figures={preview.email} sent={preview.sent} />
            </div>
          )}
        </aside>
      </div>

      <footer className="notif-automation-foot">
        <span className="ui-hint">
          {form.enabled ? (
            <Badge color="green" dot>
              {t("Next: {{when}}", { when: automation.schedule })}
            </Badge>
          ) : (
            <Badge color="slate" dot>
              {t("Disabled")}
            </Badge>
          )}
          {last?.at && (
            <span className="notif-last-run">
              {t("Last send")} : {new Date(last.at).toLocaleString()} · {t("{{push}} notification(s) and {{email}} email(s) sent", { push: last.push?.sent || 0, email: last.email?.sent || 0 })}
              {last.email?.stopped === "quota" ? ` · ${t("emails stopped: monthly email limit almost reached")}` : ""}
            </span>
          )}
        </span>
        <div className="notif-actions">
          <button type="button" className="ui-btn ui-btn-secondary" disabled={Boolean(busy)} onClick={() => run("preview", { dryRun: true })}>
            <i className="ri-eye-line" aria-hidden="true"></i>
            {busy === "preview" ? t("Calculating…") : t("Preview")}
          </button>
          {withEmail && (
            <span className="email-auto-test">
              <input type="email" className="ui-input" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} aria-label={t("Test address")} placeholder={t("Test address")} />
              <button type="button" className="ui-btn ui-btn-secondary" disabled={Boolean(busy) || !testEmail.includes("@")} onClick={() => run("test", { testEmail: testEmail.trim() })}>
                <i className="ri-mail-send-line" aria-hidden="true"></i>
                {busy === "test" ? t("Sending…") : t("Send me a test email")}
              </button>
            </span>
          )}
          <button type="button" className="ui-btn ui-btn-secondary" disabled={Boolean(busy)} onClick={() => setConfirmRun(true)}>
            <i className="ri-send-plane-line" aria-hidden="true"></i>
            {t("Send now")}
          </button>
          <button type="button" className="ui-btn" disabled={!dirty || saving} onClick={() => save()}>
            {saving ? t("Saving…") : t("Save")}
          </button>
        </div>
      </footer>

      <ConfirmDialog open={confirmRun} onClose={() => setConfirmRun(false)} onConfirm={() => run("run", {})} confirmLabel={t("Send now")}>
        <strong>{t("Send this reminder now?")}</strong>
        <p>{t("Everyone in the audience receives it right away, outside the usual schedule (within the limits set above).")}</p>
      </ConfirmDialog>
    </section>
  );
};

export default ReminderAutomation;
